import * as React from 'react';
import Box from '@mui/material/Box';
import Stepper from '@mui/material/Stepper';
import Step from '@mui/material/Step';
import StepLabel from '@mui/material/StepLabel';
import Typography from '@mui/material/Typography';
import dayjs from 'dayjs';
import { DATE_FORMAT } from '../../../constants';
import { OrderStatus } from '../../../types';
import MobileStepper from '@mui/material/MobileStepper';
import { useTheme } from '@mui/material';

interface StepConfig {
	status: OrderStatus,
	label: string,
	altIdx?: number,
}

const steps: StepConfig[] = [
	{ status: OrderStatus.Created, label: 'Order created' },
	{ status: OrderStatus.Prepared, label: 'Items availability' },
	{ status: OrderStatus.Paid, label: 'Checkout' },
	{ status: OrderStatus.Shipped, label: 'Shipped' },
	{ status: OrderStatus.Cancelled, label: 'Cancelled', altIdx: 1 },
	{ status: OrderStatus.Rejected, label: 'Rejected', altIdx: 1 },
];

interface Props {
	orderStatus: OrderStatus;
	orderDate: string;
	inProgress?: boolean;
}

export default function StatusStepper(props: Props) {
	const stepIdx = steps.findIndex((step) => step.status === props.orderStatus);
	const activeStep = steps[stepIdx].altIdx ?? stepIdx;
	const mainSteps = steps.filter(({altIdx}) => !Number.isInteger(altIdx));

	return (
		<Box sx={{ width: '100%' }}>
			<Stepper activeStep={props.inProgress ? activeStep + 1 : activeStep} alternativeLabel>
				{mainSteps.map((step, index) => {
					const stepProps: { completed?: boolean } = {completed: activeStep >= index};
					const labelProps: { optional?: React.ReactNode, error?: boolean } = {};
					let label = step.label; 

					if (props.orderStatus === OrderStatus.Rejected && index === 1) {
						labelProps.error = true;
						label = 'Rejected';
					}

					if (props.orderStatus === OrderStatus.Cancelled && index === 1) {
						labelProps.error = true;
						label = 'Cancelled';
					}

					if (step.status === OrderStatus.Created) {
						labelProps.optional = (
							<Typography variant="caption" noWrap>
								{dayjs(props.orderDate).format(DATE_FORMAT)}
							</Typography>
						);
					}

					return (
						<Step
							key={label}
							{...stepProps}
						>
							<StepLabel {...labelProps}>{label}</StepLabel>
						</Step>
					);
				})}
			</Stepper>
		</Box>
	);
}

interface ListViewStatusStepperProps {
	orderStatus: OrderStatus;
	isCancelled?: boolean;
}

export const ListViewStatusStepper = ({orderStatus, isCancelled}: ListViewStatusStepperProps) => {
	const steps = [OrderStatus.Prepared, OrderStatus.Paid, OrderStatus.Shipped];
	const activeStep = steps.findIndex((step) => step === orderStatus);
	const theme = useTheme();

	return <MobileStepper
		sx={{
			bgcolor: 'transparent',
			...(isCancelled ? {
				'.MuiMobileStepper-dotActive': {
					backgroundColor: theme.palette.error.main,
				},
			} : {})
		}}
		variant="dots"
		steps={3}
		activeStep={activeStep}
		position="static"
		backButton={undefined}
		nextButton={undefined}
	/>;
};
